import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * ELARA: on mobile the bar also shows before the shopper reaches the buy buttons.
 * Until she picks a variant, its button reads "Elegir talla" and scrolls to the
 * variant picker instead of adding to cart (data-mode="choose").
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {MediaQueryList} */
  #mobileQuery = window.matchMedia('(max-width: 749px)');

  /** @type {boolean} */
  #variantChosen = false;

  connectedCallback() {
    super.connectedCallback();

    this.#variantChosen = !this.#requiresSizeChoice() || new URL(window.location.href).searchParams.has('variant');
    this.dataset.sizeChosen = this.#variantChosen ? 'true' : 'false';
    this.#setupChooseMode();
    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    document.addEventListener('change', this.#handleSizeChoice, { capture: true, signal });
    document.addEventListener('click', this.#handleSizeChoice, { capture: true, signal });
    document.addEventListener('elara:size-chosen', this.#handleSizeGuideChoice, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#isChatActive()) return;
          this.#showStickyBar(false);
        } else if (this.#chooseModeAvailable()) {
          // ELARA: below the viewport on mobile - show the bar before the shopper reaches the buy buttons
          if (this.#isChatActive()) return;
          this.#showStickyBar(true);
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            if (!this.#isChatActive()) {
              this.#showStickyBar(false);
            }
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (this.refs.stickyBar.dataset.mode === 'choose') {
      this.#scrollToVariantPicker();
      return;
    }

    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // ELARA: once a variant has been picked, the bar adds to cart directly
    this.#variantChosen = !this.#requiresSizeChoice() || this.dataset.sizeChosen === 'true';
    this.refs.stickyBar.dataset.mode = this.#variantChosen ? 'add' : 'choose';

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const currentMode = this.refs.stickyBar.getAttribute('data-mode') || 'add';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.refs.stickyBar.setAttribute('data-mode', currentMode);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   * @param {boolean} beforeBuyButtons - True when the buy buttons have not been reached yet
   */
  #showStickyBar(beforeBuyButtons) {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.mode = this.#requiresSizeChoice() && !this.#variantChosen ? 'choose' : 'add';
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  #markSizeChosen() {
    this.#variantChosen = true;
    this.dataset.sizeChosen = 'true';
    this.refs.stickyBar.dataset.mode = 'add';
  }

  #handleSizeGuideChoice = (event) => {
    if (String(event.detail?.productId) === this.dataset.productId) this.#markSizeChosen();
  };

  #handleSizeChoice = (event) => {
    if (!(event.target instanceof Element)) return;
    const label = event.target.closest('label');
    const input = event.target.closest('input, select') || label?.querySelector('input, select') || (label?.htmlFor && document.getElementById(label.htmlFor));
    if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement) || input.disabled || input.getAttribute('aria-disabled') === 'true') return;
    const section = this.closest('.shopify-section');
    if (!section?.contains(input)) return;
    const picker = input.closest('variant-picker');
    const name = input.dataset.optionName || input.name || '';
    if (/color|colour|couleur/i.test(name)) return;
    if (picker?.dataset.productId === this.dataset.productId && /talla|size/i.test(name)) {
      this.#markSizeChosen();
      return;
    }
    if (!input.closest('.globo-swatch-product-detail')) return;
    const nativePicker = section.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    const sizes = [...(nativePicker?.querySelectorAll('input[data-option-name], select[name]') || [])].filter(option => /talla|size/i.test(option.dataset.optionName || option.name));
    const normalise = value => String(value || '').replace(/\s+/g, ' ').trim().toUpperCase().replace(/\bXXXL\b/g, '3XL').replace(/\bXXL\b/g, '2XL');
    const value = normalise(input.value);
    if (sizes.some(size => {
      const sizeValue = normalise(size.value);
      return value === sizeValue || value.startsWith(sizeValue + ' ') || value.startsWith(sizeValue + '(') || value.endsWith('-' + sizeValue) || value.includes('-' + sizeValue + ' ');
    })) this.#markSizeChosen();
  };

  /**
   * ELARA: the bar shows before the buy buttons only on mobile, for products with variants
   * @returns {boolean}
   */
  #requiresSizeChoice() {
    const picker = document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    return Boolean(picker && [...picker.querySelectorAll('input[data-option-name], select[name]')].some(input => /talla|size/i.test(input.dataset.optionName || input.name)));
  }

  #chooseModeAvailable() {
    return this.#mobileQuery.matches && this.dataset.variantAvailable === 'true';
  }

  /**
   * ELARA: sets the "choose" button label from the product's options and adds its styles once
   */
  #setupChooseMode() {
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    const optionsText = (variantPicker?.textContent || '').toLowerCase();
    const label = !variantPicker ? 'Comprar ahora' : optionsText.includes('talla') ? 'Elegir talla' : 'Elegir opción';
    this.refs.stickyBar.style.setProperty('--elara-choose-label', JSON.stringify(label));

    if (document.getElementById('elara-sticky-choose-styles')) return;
    const style = document.createElement('style');
    style.id = 'elara-sticky-choose-styles';
    style.textContent = `
      .sticky-add-to-cart__bar[data-mode='choose'] .add-to-cart-text,
      .sticky-add-to-cart__bar[data-mode='choose'] .add-to-cart__added { display: none; }
      .sticky-add-to-cart__bar[data-mode='choose'] .sticky-add-to-cart__button::after {
        content: var(--elara-choose-label);
        white-space: nowrap;
      }
      @media screen and (max-width: 749px) {
        .sticky-add-to-cart__bar[data-mode] .sticky-add-to-cart__button {
          width: auto;
          padding-inline: 18px;
        }
        .sticky-add-to-cart__bar[data-mode='add'] .add-to-cart-text__content {
          display: inline;
          white-space: nowrap;
        }
        .sticky-add-to-cart__bar[data-mode='add'] .sticky-add-to-cart__button .svg-wrapper {
          display: none;
        }
      }
      body:has(.sticky-add-to-cart__bar[data-stuck='true']) [class*='kl-teaser'] {
        bottom: 96px !important;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * ELARA: scrolls to the variant picker (or the buy buttons) leaving room for the sticky header
   */
  #scrollToVariantPicker() {
    const productId = this.dataset.productId;
    const section = this.closest('.shopify-section') ?? document;

    // The visible size buttons come from the Globo swatch app; the theme's variant-picker is hidden.
    // Take the first candidate that is actually laid out.
    const candidates = [
      ...section.querySelectorAll('.globo-swatch-product-detail'),
      ...section.querySelectorAll(`variant-picker[data-product-id="${productId}"] fieldset`),
      this.#getProductForm()?.closest('.buy-buttons-block'),
    ];
    const box = candidates.find((el) => el && el.getBoundingClientRect().height > 0);
    if (!box) return;

    box.scrollIntoView({
      block: 'center',
      inline: 'nearest',
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}

